import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  BankOutlined,
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ContainerOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  NodeIndexOutlined,
  NumberOutlined,
  ReloadOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import axios from "../../services/axiosInstance";
import {
  COMPLETE_RAKE,
  GET_ACTIVE_RAKE,
  WORKFLOW_STATS,
} from "../../services/apiEndPoints";
import { IActiveRake, IRakeDetail } from "../types/rake.types";
import { IWorkflowStats, IWorkflowUpload } from "../types/workflow.types";
import RakeDriversList from "./RakeDriversList";
import VehicleUploadsModal from "./VehicleUploadsModal";
import RakeDocumentsModal from "./RakeDocumentsModal";
import TripListComponent from "../trip/TripListComponent";
import { getDocTypeConfig, isPdfUrl } from "./docTypeLabels";
import { toastSuccess, toastError } from "../../utils/toast";

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const LiveRakeOperation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [active, setActive] = useState<IActiveRake | null>(null);
  const [uploads, setUploads] = useState<IWorkflowUpload[]>([]);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [docsOpen, setDocsOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const activeRes = await axios.get(GET_ACTIVE_RAKE);
      const payload = activeRes?.data;
      const activeData: IActiveRake | null =
        payload?.active && payload?.data ? payload.data : null;
      setActive(activeData);

      if (activeData) {
        const fromDate = dayjs(activeData.rakeMaster.createdAt).format(
          "YYYY-MM-DD"
        );
        const toDate = dayjs().format("YYYY-MM-DD");
        const wfRes = await axios.get(
          `${WORKFLOW_STATS}?fromDate=${fromDate}&toDate=${toDate}`
        );
        const wf: IWorkflowStats | undefined = wfRes?.data?.data;
        const startedAt = dayjs(activeData.rakeMaster.createdAt);
        const filtered = (wf?.uploads || []).filter((u) =>
          dayjs(u.createdAt).isAfter(startedAt)
        );
        filtered.sort(
          (a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix()
        );
        setUploads(filtered);
      } else {
        setUploads([]);
      }
    } catch {
      toastError("Failed to load live rake operation");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!active) return;
    const interval = window.setInterval(fetchAll, 300_000);
    return () => window.clearInterval(interval);
  }, [active, fetchAll]);

  const openVehicleModal = useCallback((vehicleNumber: string) => {
    if (!vehicleNumber) return;
    setSelectedVehicle(vehicleNumber);
    setVehicleModalOpen(true);
  }, []);

  const closeVehicleModal = useCallback(() => {
    setVehicleModalOpen(false);
  }, []);

  const handleComplete = useCallback(() => {
    if (!active?.rakeMaster?.rakeMasterId) return;
    Modal.confirm({
      title: "Complete this rake operation?",
      content:
        "Marking this rake as complete will close it for further weightment entries. This cannot be undone.",
      okText: "Yes, complete",
      okButtonProps: { type: "primary" },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setCompleting(true);
          await axios.post(COMPLETE_RAKE(active.rakeMaster.rakeMasterId));
          toastSuccess("Rake operation completed");
          await fetchAll();
        } catch (error: any) {
          const status = error?.response?.status;
          if (status === 404) {
            toastError("Active rake not found");
          } else {
            toastError("Failed to complete rake");
          }
        } finally {
          setCompleting(false);
        }
      },
    });
  }, [active, fetchAll]);

  const stats = useMemo(() => {
    const details = active?.rakeDetails ?? [];
    const totals = details.reduce(
      (acc, r) => {
        acc.actualBags += Number(r.actualBags || 0);
        acc.rrBags += Number(r.rrBags || 0);
        acc.chargeWeight += Number(r.chargeWeight || 0);
        acc.actualWeight += Number(r.actualWeight || 0);
        return acc;
      },
      { actualBags: 0, rrBags: 0, chargeWeight: 0, actualWeight: 0 }
    );
    const wagonsLoaded = details.filter(
      (r) => Number(r.actualBags || 0) > 0 || Number(r.actualWeight || 0) > 0
    ).length;

    const docsByType: Record<string, number> = {};
    const vehicleSet = new Set<string>();
    uploads.forEach((u) => {
      docsByType[u.docType] = (docsByType[u.docType] || 0) + 1;
      if (u.vehicleNumber) vehicleSet.add(u.vehicleNumber);
    });

    return {
      wagons: details.length,
      wagonsLoaded,
      ...totals,
      shortage: totals.chargeWeight - totals.actualWeight,
      docsTotal: uploads.length,
      vehicles: vehicleSet.size,
      docsByType,
    };
  }, [active, uploads]);

  const wagonColumns = useMemo(
    () => [
      {
        title: "S.No",
        dataIndex: "sno",
        key: "sno",
        width: 60,
      },
      {
        title: "Wagon",
        dataIndex: "wagonNo",
        key: "wagonNo",
        render: (v: string) =>
          v ? <Text strong>{v}</Text> : <Text type="secondary">--</Text>,
      },
      {
        title: "Type",
        dataIndex: "typeOfWagon",
        key: "typeOfWagon",
        render: (v: string) =>
          v ? <Tag color="geekblue">{v}</Tag> : <Text type="secondary">--</Text>,
      },
      {
        title: "Variety",
        dataIndex: "variety",
        key: "variety",
        render: (v: string) =>
          v ? <Tag color="purple">{v}</Tag> : <Text type="secondary">--</Text>,
      },
      {
        title: "Actual Bags",
        dataIndex: "actualBags",
        key: "actualBags",
        align: "right" as const,
        render: (v: number) => (v ?? 0).toLocaleString(),
      },
      {
        title: "Actual Weight",
        dataIndex: "actualWeight",
        key: "actualWeight",
        align: "right" as const,
        render: (v: number) => `${(v ?? 0).toLocaleString()} kg`,
      },
      {
        title: "Status",
        key: "status",
        align: "center" as const,
        render: (_: unknown, r: IRakeDetail) => {
          const loaded =
            Number(r.actualBags || 0) > 0 || Number(r.actualWeight || 0) > 0;
          return loaded ? (
            <Tag color="success">Loaded</Tag>
          ) : (
            <Tag color="default">Pending</Tag>
          );
        },
      },
    ],
    []
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Loading live operation...</Text>
        </div>
      </div>
    );
  }

  if (!active) {
    return (
      <Card
        style={{
          borderRadius: 14,
          border: "1px dashed #cbd5e1",
          background: "#f8fafc",
        }}
        styles={{ body: { padding: 48 } }}
      >
        <div style={{ textAlign: "center" }}>
          <ContainerOutlined
            style={{ fontSize: 56, color: "#cbd5e1", marginBottom: 16 }}
          />
          <Title level={4} style={{ color: "#475569", margin: 0 }}>
            No active rake operation
          </Title>
          <Text
            type="secondary"
            style={{ display: "block", marginTop: 6, marginBottom: 20 }}
          >
            There is no rake currently in progress. Upload a new rake to start
            an operation.
          </Text>
          <Space>
            <Button onClick={fetchAll} icon={<ReloadOutlined />}>
              Refresh
            </Button>
            <Button
              icon={<FileDoneOutlined />}
              onClick={() => navigate("/rake-workflow-documents")}
            >
              Workflow Documents
            </Button>
            <Button type="primary" onClick={() => navigate("/rakes")}>
              Go to Rakes
            </Button>
          </Space>
        </div>
      </Card>
    );
  }

  const { rakeMaster } = active;

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* ─── Header ─────────────────────────────────────── */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #064e3b 0%, #065f46 60%, #047857 100%)",
          borderRadius: 16,
          padding: "26px 32px",
          marginBottom: 20,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -40,
            right: -20,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(16,185,129,0.15)",
          }}
        />
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Space size={16} align="center">
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                  color: "#fff",
                }}
              >
                <ThunderboltOutlined />
              </div>
              <div>
                <Space size={8} align="center" wrap>
                  <Tag
                    icon={<SyncOutlined spin />}
                    color="success"
                    style={{ margin: 0, borderRadius: 6, fontWeight: 600 }}
                  >
                    Live
                  </Tag>
                  <Text style={{ color: "#a7f3d0", fontSize: 12 }}>
                    Started {dayjs(rakeMaster.createdAt).fromNow()}
                  </Text>
                </Space>
                <Title
                  level={3}
                  style={{ color: "#fff", margin: "6px 0 2px" }}
                >
                  Rake {rakeMaster.rrNumber || rakeMaster.rakeMasterId}
                </Title>
                <Space size={14} wrap>
                  {rakeMaster.fnrNumber && (
                    <Text style={{ color: "#d1fae5", fontSize: 12 }}>
                      <NumberOutlined style={{ marginRight: 4 }} />
                      FNR {rakeMaster.fnrNumber}
                    </Text>
                  )}
                  {rakeMaster.company && (
                    <Text style={{ color: "#d1fae5", fontSize: 12 }}>
                      <BankOutlined style={{ marginRight: 4 }} />
                      {rakeMaster.company}
                    </Text>
                  )}
                  {rakeMaster.date && (
                    <Text style={{ color: "#d1fae5", fontSize: 12 }}>
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      {dayjs(rakeMaster.date).format("MMM D, YYYY")}
                    </Text>
                  )}
                </Space>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                ghost
                icon={<ReloadOutlined />}
                onClick={fetchAll}
                loading={loading}
                style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}
              >
                Refresh
              </Button>
              <Button
                icon={<FolderOpenOutlined />}
                onClick={() => setDocsOpen(true)}
              >
                Rake Documents
              </Button>
              <Button
                icon={<FileDoneOutlined />}
                onClick={() => navigate("/rake-workflow-documents")}
              >
                Workflow Documents
              </Button>
              <Button
                onClick={() => navigate(`/rakes/${rakeMaster.rakeMasterId}`)}
              >
                Open rake details
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={completing}
                onClick={handleComplete}
              >
                Complete operation
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* ─── KPI strip ──────────────────────────────────── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Kpi
          label="Wagons loaded"
          value={`${stats.wagonsLoaded} / ${stats.wagons}`}
          color="#1677ff"
          bg="linear-gradient(135deg, #eff6ff, #dbeafe)"
          border="#93c5fd"
          icon={<ContainerOutlined />}
        />
        <Kpi
          label="Actual bags"
          value={stats.actualBags.toLocaleString()}
          color="#8b5cf6"
          bg="linear-gradient(135deg, #f5f3ff, #ede9fe)"
          border="#c4b5fd"
          icon={<NumberOutlined />}
        />
        <Kpi
          label="Actual weight"
          value={`${stats.actualWeight.toLocaleString()} kg`}
          color="#10b981"
          bg="linear-gradient(135deg, #f0fdf4, #dcfce7)"
          border="#6ee7b7"
          icon={<ThunderboltOutlined />}
        />
        <Kpi
          label="Workflow uploads"
          value={stats.docsTotal}
          color="#f59e0b"
          bg="linear-gradient(135deg, #fffbeb, #fef3c7)"
          border="#fcd34d"
          icon={<FileTextOutlined />}
          suffix={`${stats.vehicles} vehicles`}
        />
      </Row>

      {/* ─── Rake drivers ───────────────────────────────── */}
      <RakeDriversList
        rakeMasterId={rakeMaster.rakeMasterId}
        autoRefreshMs={300_000}
      />

      {/* ─── Tabbed content ─────────────────────────────── */}
      <Card
        className="content-card"
        style={{ borderRadius: 14 }}
        styles={{ body: { padding: "0 24px 24px" } }}
      >
        <Tabs
          defaultActiveKey="trips"
          items={[
            {
              key: "trips",
              label: (
                <Space size={6}>
                  <NodeIndexOutlined />
                  Trips
                </Space>
              ),
              children: (
                <div style={{ paddingTop: 4 }}>
                  <TripListComponent rakeMasterId={rakeMaster.rakeMasterId} />
                </div>
              ),
            },
            {
              key: "uploads",
              label: (
                <Space size={6}>
                  <FileTextOutlined />
                  Uploads
                  <Tag style={{ margin: 0, borderRadius: 10, fontSize: 11 }}>
                    {stats.docsTotal}
                  </Tag>
                </Space>
              ),
              children: (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <Text style={{ color: "#94a3b8", fontSize: 12 }}>
                      {stats.docsTotal} document{stats.docsTotal === 1 ? "" : "s"} from{" "}
                      {stats.vehicles} vehicle{stats.vehicles === 1 ? "" : "s"}
                    </Text>
                    <Space wrap>
                      {Object.entries(stats.docsByType).map(([t, count]) => {
                        const cfg = getDocTypeConfig(t);
                        return (
                          <Tag key={t} color={cfg.color} style={{ margin: 0, borderRadius: 6 }}>
                            {cfg.label}: {count}
                          </Tag>
                        );
                      })}
                    </Space>
                  </div>

                  {uploads.length === 0 ? (
                    <Empty description="No workflow uploads yet for this operation" />
                  ) : (
                    <Row gutter={[12, 12]}>
                      {uploads.map((u) => {
                        const cfg = getDocTypeConfig(u.docType);
                        return (
                          <Col xs={12} sm={8} md={6} lg={4} key={u.workflowTaskId}>
                            <div
                              onClick={() => openVehicleModal(u.vehicleNumber)}
                              style={{
                                cursor: "pointer",
                                border: "1px solid #e2e8f0",
                                borderRadius: 12,
                                overflow: "hidden",
                                background: "#fff",
                                transition: "all 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = "#1677ff";
                                e.currentTarget.style.boxShadow = "0 4px 12px rgba(22,119,255,0.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = "#e2e8f0";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <div
                                style={{
                                  height: 120,
                                  background: "#f8fafc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                }}
                              >
                                {u.s3Url && !isPdfUrl(u.s3Url) ? (
                                  <img
                                    src={u.s3Url}
                                    alt={u.docType}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                                  />
                                ) : (
                                  <FileTextOutlined style={{ fontSize: 32, color: "#cbd5e1" }} />
                                )}
                              </div>
                              <div style={{ padding: "10px 12px" }}>
                                <Tag color={cfg.color} style={{ margin: 0, marginBottom: 6, borderRadius: 6, fontSize: 11 }}>
                                  {cfg.label}
                                </Tag>
                                <Text strong style={{ display: "block", fontSize: 12, color: "#1677ff" }} ellipsis>
                                  <CarOutlined style={{ marginRight: 4 }} />
                                  {u.vehicleNumber}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
                                  {dayjs(u.createdAt).fromNow()}
                                </Text>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </>
              ),
            },
            {
              key: "wagons",
              label: (
                <Space size={6}>
                  <ContainerOutlined />
                  Wagons
                  <Tag style={{ margin: 0, borderRadius: 10, fontSize: 11 }}>
                    {stats.wagons}
                  </Tag>
                </Space>
              ),
              children: (
                <Table<IRakeDetail>
                  rowKey={(r) => `${r.rakeMasterId}-${r.sno}`}
                  size="small"
                  columns={wagonColumns as any}
                  dataSource={active.rakeDetails}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50"],
                  }}
                  scroll={{ x: "max-content" }}
                  locale={{ emptyText: <Empty description="No wagons in this rake" /> }}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* ─── Vehicle uploads modal ──────────────────────── */}
      <VehicleUploadsModal
        open={vehicleModalOpen}
        rakeMasterId={rakeMaster.rakeMasterId}
        vehicleNumber={selectedVehicle}
        onClose={closeVehicleModal}
      />

      <RakeDocumentsModal
        open={docsOpen}
        rakeMasterId={rakeMaster.rakeMasterId}
        onClose={() => setDocsOpen(false)}
      />
    </div>
  );
};

const Kpi = ({
  label,
  value,
  color,
  bg,
  border,
  icon,
  suffix,
}: {
  label: string;
  value: string | number;
  color: string;
  bg: string;
  border: string;
  icon: React.ReactNode;
  suffix?: string;
}) => (
  <Col xs={12} sm={12} md={6}>
    <Card
      className="stat-card"
      style={{ background: bg, border: `1px solid ${border}` }}
      styles={{ body: { padding: "14px 16px" } }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: `${color}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <Tooltip title={String(value)}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value}
            </div>
          </Tooltip>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {label}
          </div>
          {suffix && (
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
              {suffix}
            </div>
          )}
        </div>
      </div>
    </Card>
  </Col>
);

export default LiveRakeOperation;
